/**
 * LLM / AI-search referral detection for Journal Party.
 *
 * Visitors arriving from AI assistants (ChatGPT, Perplexity, Claude, Gemini,
 * etc.) are invisible in Google Search Console -- the user clicked through the
 * AI answer, not a Google result. This module detects those arrivals from the
 * document referrer (and any `utm_source` / `ref` the assistant appended) and
 * normalizes them to a stable source label, so trackingManager can fire a
 * dedicated GA4 `llm_referral` event and a server-side warehouse row.
 *
 * Detection is pure and side-effect free; the once-per-session guard and the
 * GA4 emit live in AcquisitionCapture so this stays unit-testable.
 */

export interface LlmReferralResult {
  /** Stable, lowercase source label, e.g. "chatgpt" | "perplexity" | "claude". */
  source: string;
  /** How it was detected: the referrer host or the param value that matched. */
  detail: string;
  /** "referrer" when matched from document.referrer, "param" from a query param. */
  via: 'referrer' | 'param';
}

/**
 * Host suffixes mapped to a canonical source label. Matched against the
 * referrer hostname and against any dotted `utm_source` value. Order does not
 * matter -- the first suffix match wins.
 */
const LLM_HOST_SOURCES: ReadonlyArray<{ suffix: string; source: string }> = [
  { suffix: 'chatgpt.com', source: 'chatgpt' },
  { suffix: 'chat.openai.com', source: 'chatgpt' },
  { suffix: 'openai.com', source: 'chatgpt' },
  { suffix: 'perplexity.ai', source: 'perplexity' },
  { suffix: 'claude.ai', source: 'claude' },
  { suffix: 'gemini.google.com', source: 'gemini' },
  { suffix: 'bard.google.com', source: 'gemini' },
  { suffix: 'copilot.microsoft.com', source: 'copilot' },
  { suffix: 'you.com', source: 'you' },
  { suffix: 'poe.com', source: 'poe' },
  { suffix: 'deepseek.com', source: 'deepseek' },
  { suffix: 'meta.ai', source: 'meta_ai' },
  { suffix: 'phind.com', source: 'phind' },
  { suffix: 'grok.com', source: 'grok' },
  { suffix: 'x.ai', source: 'grok' },
  { suffix: 'mistral.ai', source: 'mistral' },
];

/**
 * Bare token values (no dot) that AI assistants set as `utm_source` / `ref`.
 * Keyed by the normalized token.
 */
const LLM_TOKEN_SOURCES: Readonly<Record<string, string>> = {
  chatgpt: 'chatgpt',
  openai: 'chatgpt',
  perplexity: 'perplexity',
  claude: 'claude',
  anthropic: 'claude',
  gemini: 'gemini',
  bard: 'gemini',
  copilot: 'copilot',
  bingchat: 'copilot',
  poe: 'poe',
  deepseek: 'deepseek',
  metaai: 'meta_ai',
  phind: 'phind',
  grok: 'grok',
  mistral: 'mistral',
};

/** Query params an assistant may use to advertise itself as the source. */
const SOURCE_PARAM_KEYS = ['utm_source', 'ref', 'ref_source', 'source'] as const;

function normalizeToken(value: string): string {
  return value.trim().toLowerCase().replace(/[\s_-]+/g, '');
}

function matchHostSuffix(host: string): string | null {
  const normalized = host.trim().toLowerCase().replace(/\.$/, '');
  if (!normalized) return null;
  for (const { suffix, source } of LLM_HOST_SOURCES) {
    if (normalized === suffix || normalized.endsWith(`.${suffix}`)) {
      return source;
    }
  }
  return null;
}

/** Resolve a source from any value, whether a hostname or a bare token. */
function matchSource(value: string | null | undefined): string | null {
  if (!value) return null;
  const trimmed = value.trim().toLowerCase();
  if (!trimmed) return null;

  if (trimmed.includes('.')) {
    const host = matchHostSuffix(trimmed);
    if (host) return host;
  }

  return LLM_TOKEN_SOURCES[normalizeToken(trimmed)] ?? null;
}

function getReferrerHost(referrer: string): string | null {
  try {
    return new URL(referrer).hostname || null;
  } catch {
    return null;
  }
}

/**
 * Detect whether a visit originated from an AI assistant.
 *
 * Checks the referrer host first (the strongest signal), then falls back to
 * source-advertising query params. Same-origin referrers are ignored so an
 * internal SPA navigation is never mistaken for an AI arrival.
 */
export function detectLlmReferral(input: {
  referrer?: string | null;
  url?: string | null;
  currentHost?: string | null;
}): LlmReferralResult | null {
  const referrer = input.referrer?.trim() || '';
  const referrerHost = referrer ? getReferrerHost(referrer) : null;

  // Skip same-origin referrers (internal navigation).
  if (referrerHost && input.currentHost && referrerHost === input.currentHost) {
    return null;
  }

  if (referrerHost) {
    const source = matchHostSuffix(referrerHost);
    if (source) {
      return { source, detail: referrerHost, via: 'referrer' };
    }
  }

  if (input.url) {
    try {
      const params = new URL(input.url).searchParams;
      for (const key of SOURCE_PARAM_KEYS) {
        const raw = params.get(key);
        const source = matchSource(raw);
        if (source) {
          return { source, detail: `${key}=${raw}`, via: 'param' };
        }
      }
    } catch {
      // Ignore malformed URLs.
    }
  }

  return null;
}

/** Detect an LLM referral from the live browser environment. Null on the server. */
export function detectLlmReferralFromBrowser(): LlmReferralResult | null {
  if (typeof window === 'undefined' || typeof document === 'undefined') return null;
  return detectLlmReferral({
    referrer: document.referrer,
    url: window.location.href,
    currentHost: window.location.hostname,
  });
}
